<template>
    <div class="import-feedback-drawer-fix-issues-step">
        <pendo-card class="import-feedback-drawer-fix-issues-step__container">
            <h3>Fix issues</h3>
            <p>
                Some of the values provided don't match the expected values for a feedback item.
            </p>
            <template v-if="missingVisitorAndAccountCount > 0">
                <pendo-alert type="warning">
                    <ul class="import-feedback-drawer-fix-issues-step__alert-list">
                        <li
                            class="import-feedback-drawer-fix-issues-step__alert-list-item">
                            {{ missingVisitorAndAccountText }}
                        </li>
                    </ul>
                </pendo-alert>
                <br>
            </template>
            <template v-if="hasMissingIssues">
                <pendo-alert type="info">
                    <ul class="import-feedback-drawer-fix-issues-step__alert-list">
                        <li
                            v-for="(missingIssueText, missingIssueTextIndex) in missingIssuesTexts"
                            :key="missingIssueTextIndex"
                            class="import-feedback-drawer-fix-issues-step__alert-list-item">
                            {{ missingIssueText }}
                        </li>
                    </ul>
                    <div class="import-feedback-drawer-fix-issues-step__alert-content">
                        <pendo-toggle
                            :value="createMissingVisitorsAndAccounts"
                            data-cy="import-feedback-drawer-fix-issues-step-create-accounts-visitors-toggle"
                            @input="onCreateMissingVisitorsAndAccountsChange" />
                        <span class="import-feedback-drawer-fix-issues-step__toggle-label">
                            Create accounts and visitors if they don't exist
                        </span>
                    </div>
                </pendo-alert>
                <br>
            </template>
            <pendo-table
                v-if="hasFilteredIssues"
                :data="filteredIssues"
                :columns="fixIssuesColumns"
                :group-config="{
                    enabled: groupByColumns,
                    groupKey: 'kind',
                    expandable: true,
                    sortChildren: true,
                    expandedKeys: groupExpandedKeys,
                }"
                title="Fix issues"
                max-height="1000"
                @group-expand-change="handleGroupExpandChange">
                <template #headerLeft>
                    Fix issues
                </template>
                <template #groupRow="{ row }">
                    <div class="import-feedback-drawer-fix-issues-step__section-header">
                        {{ sectionHeaders[row.kind] }}
                    </div>
                </template>
                <template #valueInFile="{ row }">
                    <div class="import-feedback-drawer-fix-issues-step__expected-value">
                        <p v-if="row.value === '' && row.kind === 'app'">
                            Empty value <span> * </span>
                        </p>
                        <p v-else>
                            {{ row.value }} <span> * </span>
                        </p>
                        <pendo-icon
                            type="arrow-right"
                            size="16" />
                    </div>
                </template>
                <template #expectedValueHeader="{ column }">
                    <div class="import-feedback-drawer-fix-issues-step__expected-value-header">
                        <img
                            class="filled-icon"
                            width="24"
                            height="24"
                            src="@img/icons/pendo-logo-icon.svg">
                        <span>
                            {{ column.label }}
                        </span>
                    </div>
                </template>
                <template #expectedValue="{ row, rowIndex }">
                    <div v-if="row.kind === 'app'">
                        <pendo-multiselect
                            label-key="displayName"
                            full-width
                            :value="row.selectedValue"
                            :options="apps"
                            @select="(value) => onSelectValue(value, row, rowIndex)">
                            <template #selectedLabel="{ option }">
                                <pendo-icon-option :option="option" />
                            </template>
                            <template #option="{ option }">
                                <pendo-icon-option :option="option" />
                            </template>
                        </pendo-multiselect>
                    </div>
                    <div v-else>
                        <GroupDropdown
                            :value="row.selectedValue"
                            :is-idea-group="true"
                            :is-import-feedback="true"
                            @input="(value) => onSelectValue(value, row, rowIndex)" />
                    </div>
                </template>
                <template #fixIssuesStatus="{ row }">
                    <div class="import-feedback-drawer-fix-issues-step__fix-issues-status">
                        <pendo-icon
                            size="14"
                            :type="isExpectedValueSelected(row).icon"
                            :color="isExpectedValueSelected(row).color" />
                        <span> {{ isExpectedValueSelected(row).label }} </span>
                    </div>
                </template>
            </pendo-table>
        </pendo-card>
    </div>
</template>
<script>
import {
    PendoAlert,
    PendoTable,
    PendoCard,
    PendoIcon,
    PendoMultiselect,
    PendoIconOption,
    PendoToggle,
} from '@pendo/components';
import GroupDropdown from '@/common/group-dropdown/group-dropdown.vue';
import { mapGetters } from 'vuex';

const SECTION_HEADERS = {
    app: 'App name',
    productArea: 'Product area',
};

export default {
    components: {
        PendoAlert,
        PendoTable,
        PendoCard,
        PendoIcon,
        PendoMultiselect,
        PendoIconOption,
        PendoToggle,
        GroupDropdown,
    },
    props: {
        filteredIssues: {
            type: Array,
            required: true,
        },
        createMissingVisitorsAndAccounts: {
            type: Boolean,
            default: false,
        },
        missingAccountsCount: {
            type: Number,
            default: 0,
        },
        missingVisitorsCount: {
            type: Number,
            default: 0,
        },
        mismatchVisitorsAndAccountsCount: {
            type: Number,
            default: 0,
        },
        missingVisitorAndAccountCount: {
            type: Number,
            default: 0,
        },
    },
    emits: ['update-issue', 'create-accounts-visitors-change', 'create-missing-visitors-and-accounts-change'],
    data () {
        return {
            fixIssuesColumns: [
                {
                    label: 'Values in your file',
                    prop: 'valueInFile',
                    minWidth: 200,
                },
                {
                    label: 'Expected values',
                    prop: 'expectedValue',
                    minWidth: 220,
                },
                {
                    label: 'Status',
                    prop: 'fixIssuesStatus',
                    minWidth: 190,
                },
            ],
            sectionHeaders: SECTION_HEADERS,
            groupExpandedKeys: Object.keys(SECTION_HEADERS),
            groupByColumns: true,
        };
    },
    computed: {
        ...mapGetters({
            apps: 'vocItems/listenAppOptions',
        }),
        hasFilteredIssues () {
            return this.filteredIssues.length > 0;
        },
        hasMissingIssues () {
            return (
                this.missingAccountsCount > 0 ||
                this.missingVisitorsCount > 0 ||
                this.mismatchVisitorsAndAccountsCount > 0
            );
        },
        missingIssuesTexts () {
            return [this.missingAccountsText, this.missingVisitorsText, this.mismatchVisitorsAndAccountsText];
        },
        missingAccountsText () {
            const accountText =
                this.missingAccountsCount === 1 ? 'account does not exist in Pendo' : 'accounts do not exist in Pendo';

            return `${this.missingAccountsCount} ${accountText}`;
        },
        missingVisitorsText () {
            const visitorText =
                this.missingVisitorsCount === 1 ? 'visitor does not exist in Pendo' : 'visitors do not exist in Pendo';

            return `${this.missingVisitorsCount} ${visitorText}`;
        },
        mismatchVisitorsAndAccountsText () {
            const mismatchText = 'visitor account(s) do not match the records in Pendo';

            return `${this.mismatchVisitorsAndAccountsCount} ${mismatchText}`;
        },
        missingVisitorAndAccountText () {
            let variableText = 'rows are missing account or visitor data';
            if (this.missingVisitorAndAccountCount === 1) {
                variableText = 'row is missing account or visitor data';
            }

            return `${this.missingVisitorAndAccountCount} ${variableText}. You can still import the feedback, but it won't be associated with a visitor or account.`;
        },
        isExpectedValueSelected () {
            return (row) => {
                if (row.selectedValue) {
                    return { label: 'Success', icon: 'check', color: 'var(--palette-green-90)' };
                }

                return { label: 'Value invalid', icon: 'info', color: 'var(--palette-orange-70)' };
            };
        },
    },
    methods: {
        onSelectValue (value, row) {
            row.selectedValue = value;
            this.$emit('update-issue', {
                row,
                value,
            });
        },
        handleGroupExpandChange ({ groupExpandedKeys }) {
            this.groupExpandedKeys = groupExpandedKeys;
        },
        onCreateMissingVisitorsAndAccountsChange (value) {
            this.$emit('create-missing-visitors-and-accounts-change', value);
        },
    },
};
</script>

<style lang="less" scoped>
.import-feedback-drawer-fix-issues-step {
    display: flex;
    flex-direction: column;
    align-items: center;

    &__container {
        width: 70%;
        padding: 12px 20px 20px 20px;

        h3 {
            margin: 0;
            font-size: 22px;
            font-weight: 600;
        }

        p {
            margin: 8px 0 22px 0;
            font-size: 14px;
            line-height: 1.3;
        }
    }

    &__section-header {
        font-weight: 600;
    }

    &__expected-value {
        display: flex;
        align-items: center;

        p {
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex-shrink: 1;
            flex-grow: 1;

            span {
                margin-left: -2px;
                font-weight: 400;
                color: var(--palette-red-60);
            }
        }

        .pendo-icon {
            margin-left: auto;
            flex-shrink: 0;
            flex-grow: 0;
        }
    }

    &__expected-value-header {
        display: flex;
        align-items: center;

        .filled-icon {
            margin-right: 8px;
        }
    }

    &__fix-issues-status {
        display: flex;
        align-items: center;

        .pendo-icon {
            margin-right: 8px;
        }

        &--not-fixed {
            color: var(--palette-gray-70);
        }
    }

    :deep(.pendo-multiselect__trigger--searchable.is-active) {
        outline: none;
        border-color: var(--palette-gray-100);
    }

    &__alert-list {
        gap: 12px;
        padding-left: 0;
        list-style-position: inside;
        font-weight: bold;
    }

    &__alert-list-item::before {
        margin-left: -8px;
        content: '';
    }

    &__alert-content {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 8px;
    }

    &__toggle-label {
        font-size: 14px;
        color: var(--palette-gray-100);
        font-weight: 400;
        flex: 1;
    }
}
</style>
